import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, IconButton, Link, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  SortableContext,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Comic, ComicGroup, GROUPS_CONTAINER, LibraryShelf, LibraryTile, groupDragId } from '../types';
import ShelfTile, { TileProgress } from './ShelfTile';

interface GroupShelfProps {
  shelf: LibraryShelf;
  isReadOnly: boolean;
  isMobile: boolean;
  // My order, signed in
  canDrag: boolean;
  tileCover: (tile: LibraryTile) => string | undefined;
  tileProgress: (tile: LibraryTile) => TileProgress | undefined;
  onOpenTile: (tile: LibraryTile) => void;
  onTileMenu: (e: React.MouseEvent<HTMLButtonElement>, tile: LibraryTile) => void;
  onToggleRead: (comic: Comic) => void;
  onGroupMenu: (e: React.MouseEvent<HTMLButtonElement>, group: ComicGroup) => void;
  // 'grid' wraps on every width (a section's own page); 'auto' scrolls
  // sideways on phones
  layout?: 'auto' | 'grid';
  // The section's own page, when it has one; the heading links there
  sectionHref?: string;
  onOpenSection?: () => void;
  hideHeading?: boolean;
}

// One group: a heading over its covers. A grid on desktop, a sideways
// scrolling row on phones.
const GroupShelf = ({
  shelf,
  isReadOnly,
  isMobile,
  canDrag,
  tileCover,
  tileProgress,
  onOpenTile,
  onTileMenu,
  onToggleRead,
  onGroupMenu,
  layout = 'auto',
  sectionHref,
  onOpenSection,
  hideHeading = false,
}: GroupShelfProps) => {
  const { group } = shelf;
  const row = layout === 'auto' && isMobile;
  // The section page's back gesture comes back here
  const linkProps = sectionHref
    ? { component: RouterLink, to: sectionHref, state: { fromShelf: true }, onClick: onOpenSection }
    : null;
  // The automatic shelves always sit at the bottom
  const groupDraggable = canDrag && group !== null;

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: groupDragId(group?.id ?? shelf.key),
    disabled: !groupDraggable,
    data: { container: group ? GROUPS_CONTAINER : 'auto' },
  });

  return (
    <Box
      ref={setNodeRef}
      component="section"
      sx={{
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        position: 'relative',
        zIndex: isDragging ? 2 : 'auto',
      }}
    >
      {!hideHeading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
            pb: 0.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {groupDraggable && (
            <IconButton
              ref={setActivatorNodeRef}
              size="small"
              aria-label={`Drag ${shelf.title}`}
              {...attributes}
              {...listeners}
              sx={{
                color: 'text.secondary',
                cursor: 'grab',
                touchAction: 'none',
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              columnGap: 1.5,
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            <Typography variant="h5" component="h2" sx={{ lineHeight: 1.2 }}>
              {linkProps ? (
                <Link {...linkProps} color="inherit" underline="hover">
                  {shelf.title}
                </Link>
              ) : (
                shelf.title
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {shelf.meta}
            </Typography>
          </Box>

          {linkProps && (
            <Button
              {...linkProps}
              size="small"
              color="inherit"
              endIcon={<ChevronRightIcon />}
              aria-label={`See all of ${shelf.title}`}
              sx={{ ml: 'auto', flexShrink: 0, whiteSpace: 'nowrap', color: 'text.secondary', textTransform: 'none' }}
            >
              See all
            </Button>
          )}

          {group && !isReadOnly && (
            <IconButton
              size="small"
              aria-label={`${shelf.title} options`}
              onClick={(e) => onGroupMenu(e, group)}
              sx={{ ml: linkProps ? 0 : 'auto' }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      <SortableContext
        items={shelf.tiles.map((tile) => tile.id)}
        strategy={row ? horizontalListSortingStrategy : rectSortingStrategy}
      >
        <Box
          sx={
            row
              ? {
                  display: 'flex',
                  gap: 1.25,
                  overflowX: 'auto',
                  // Run to the screen edge so the scroll is obvious
                  mr: -2,
                  pr: 2,
                  pb: 1,
                  '& > *': { width: 140, flexShrink: 0 },
                }
              : {
                  display: 'grid',
                  // Two across a phone on a section's page
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(auto-fill, minmax(200px, 1fr))',
                  },
                  alignItems: 'start',
                  gap: { xs: 1.5, sm: 2.25 },
                }
          }
        >
          {shelf.tiles.map((tile) => (
            <ShelfTile
              key={tile.id}
              tile={tile}
              container={shelf.key}
              coverUrl={tileCover(tile)}
              progress={tileProgress(tile)}
              isReadOnly={isReadOnly}
              draggable={canDrag}
              compact={row}
              onOpen={onOpenTile}
              onMenuClick={onTileMenu}
              onToggleRead={onToggleRead}
            />
          ))}
        </Box>
      </SortableContext>

      {shelf.tiles.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Nothing in this group yet.
        </Typography>
      )}
    </Box>
  );
};

export default GroupShelf;
